/**
 * File Upload API Contract
 *
 * Mirrors: backend/src/routes/fileUpload.routes.ts
 * 1:1 parity with existing Express routes
 *
 * SECURITY: companyId is extracted from JWT token in handlers,
 * not from request parameters, to prevent unauthorized access.
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { RequestUploadUrlInputSchema, ConfirmUploadInputSchema, RequestReplaceInputSchema, FileIdParamsSchema, UploadUrlResponseSchema, FileUploadResponseSchema, DownloadUrlResponseSchema, FileListResponseSchema, } from "@hourly/api/fileUpload";
// ============================================
// File Upload Contracts
// ============================================
/**
 * POST /api/files/upload-url
 * Get presigned URL for uploading a new file
 * Requires: company:update permission (write access to company)
 * NOTE: companyId is extracted from JWT token, not from request
 */
export const requestUploadUrl = oc
    .route({
    method: "POST",
    path: "/files/upload-url",
    summary: "Get presigned URL for uploading a new file",
    tags: ["Files"],
})
    .input(RequestUploadUrlInputSchema)
    .output(UploadUrlResponseSchema);
/**
 * POST /api/files/confirm
 * Confirm upload after file has been uploaded to S3
 * If replaceFileId is provided, replaces existing file instead of creating new one
 * Requires: company:update permission (write access to company)
 * NOTE: companyId is extracted from JWT token, not from request
 */
export const confirmUpload = oc
    .route({
    method: "POST",
    path: "/files/confirm",
    summary: "Confirm upload after file has been uploaded to S3",
    tags: ["Files"],
})
    .input(ConfirmUploadInputSchema)
    .output(FileUploadResponseSchema);
/**
 * GET /api/files/:fileId/download-url
 * Get presigned URL for downloading a file
 * Requires: company:view permission (read access to company)
 * NOTE: companyId is extracted from JWT token, not from request
 */
export const getDownloadUrl = oc
    .route({
    method: "GET",
    path: "/files/{fileId}/download-url",
    summary: "Get presigned URL for downloading a file",
    tags: ["Files"],
})
    .input(FileIdParamsSchema)
    .output(DownloadUrlResponseSchema);
/**
 * POST /api/files/:fileId/replace
 * Get presigned URL for replacing an existing file
 * Requires: company:update permission (write access to company)
 * NOTE: companyId is extracted from JWT token, not from request
 */
export const requestReplaceUrl = oc
    .route({
    method: "POST",
    path: "/files/{fileId}/replace",
    summary: "Get presigned URL for replacing an existing file",
    tags: ["Files"],
})
    .input(FileIdParamsSchema.merge(RequestReplaceInputSchema))
    .output(UploadUrlResponseSchema);
/**
 * DELETE /api/files/:fileId
 * Delete a file
 * Requires: company:update permission (write access to company)
 * NOTE: companyId is extracted from JWT token, not from request
 */
export const deleteFile = oc
    .route({
    method: "DELETE",
    path: "/files/{fileId}",
    summary: "Delete a file",
    tags: ["Files"],
    successStatus: 204,
})
    .input(FileIdParamsSchema)
    .output(z.void());
/**
 * GET /api/files
 * List all files for the company
 * Requires: company:view permission (read access to company)
 * NOTE: companyId is extracted from JWT token, not from request
 */
export const listFiles = oc
    .route({
    method: "GET",
    path: "/files",
    summary: "List all files for the company",
    tags: ["Files"],
})
    .input(z.object({}))
    .output(FileListResponseSchema);
// ============================================
// Contract Router
// ============================================
export const fileUploadContract = {
    requestUploadUrl,
    confirmUpload,
    getDownloadUrl,
    requestReplaceUrl,
    deleteFile,
    listFiles,
};
//# sourceMappingURL=fileUpload.js.map